"use client";

import { formatPoints } from "@/lib/pointsFormat";
import { useStatementHost } from "@/lib/statementDom";
import { Typography, Empty } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import "katex/dist/katex.min.css";
import { useTranslation } from "react-i18next";

interface ProblemStatementViewerProps {
    statement?: string | null;
    problemName?: string;
    points?: number;
}

/**
 * Lightweight read-only viewer for problem statements.
 * Renders HTML content with proper styling for rich text elements
 * including KaTeX formulas, code blocks, lists, etc.
 */
export default function ProblemStatementViewer({
    statement,
    problemName,
    points,
}: ProblemStatementViewerProps) {
    const { t } = useTranslation();
    const hostRef = useStatementHost(statement);

    if (!statement) {
        return (
            <div className="problem-statement-empty">
                <Empty
                    image={
                        <FileTextOutlined
                            style={{ fontSize: 48, color: "#d9d9d9" }}
                        />
                    }
                    description={t("panel.problemStatement.empty")}
                />
            </div>
        );
    }

    return (
        <div className="problem-statement-viewer">
            {problemName && (
                <div className="problem-header">
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        {problemName}
                    </Typography.Title>
                    {points !== undefined && (
                        <Typography.Text type="secondary">
                            {t("panel.problemStatement.points", {
                                points: formatPoints(points),
                            })}
                        </Typography.Text>
                    )}
                </div>
            )}
            <div ref={hostRef} className="problem-content-host" />
            <style jsx global>{`
                .problem-statement-viewer {
                    height: 100%;
                    overflow-y: auto;
                    padding: 16px;
                    background-color: #fff;
                }
                .problem-statement-empty {
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #fafafa;
                }
                .problem-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #f0f0f0;
                }
                .problem-content {
                    line-height: 1.6;
                    color: #333;
                }
                .problem-content p {
                    margin: 0 0 0.75em 0;
                }
                .problem-content p:last-child {
                    margin-bottom: 0;
                }
                .problem-content h1,
                .problem-content h2,
                .problem-content h3 {
                    margin: 1em 0 0.5em 0;
                    color: #1a1a1a;
                }
                .problem-content h1:first-child,
                .problem-content h2:first-child,
                .problem-content h3:first-child {
                    margin-top: 0;
                }
                .problem-content h1 {
                    font-size: 1.5em;
                }
                .problem-content h2 {
                    font-size: 1.3em;
                }
                .problem-content h3 {
                    font-size: 1.1em;
                }
                .problem-content ul,
                .problem-content ol {
                    padding-left: 1.5em;
                    margin: 0 0 0.75em 0;
                }
                .problem-content li {
                    margin: 0.25em 0;
                }
                .problem-content blockquote {
                    border-left: 3px solid #1677ff;
                    padding-left: 1em;
                    margin: 0.75em 0;
                    color: #666;
                    background-color: #f9f9f9;
                    padding: 0.5em 1em;
                    border-radius: 0 4px 4px 0;
                }
                .problem-content a {
                    color: #1677ff;
                    text-decoration: none;
                }
                .problem-content a:hover {
                    text-decoration: underline;
                }
                .problem-content code {
                    background-color: #f4f4f5;
                    border-radius: 4px;
                    padding: 2px 6px;
                    font-family: "Fira Code", "Consolas", monospace;
                    font-size: 0.9em;
                    color: #e11d48;
                }
                .problem-content pre {
                    background-color: #1e1e1e;
                    color: #d4d4d4;
                    font-family: "Fira Code", "Consolas", monospace;
                    padding: 16px;
                    border-radius: 8px;
                    overflow-x: auto;
                    margin: 0.75em 0;
                    font-size: 13px;
                    line-height: 1.5;
                }
                .problem-content pre code {
                    background-color: transparent;
                    color: inherit;
                    padding: 0;
                    font-size: inherit;
                }
                .problem-content img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 4px;
                    margin: 0.5em 0;
                }
                .problem-content hr {
                    border: none;
                    border-top: 1px solid #e8e8e8;
                    margin: 1em 0;
                }
                .problem-content mark {
                    background-color: #fef08a;
                    padding: 0 2px;
                    border-radius: 2px;
                }
                .problem-content sup {
                    vertical-align: super;
                    font-size: 0.75em;
                }
                .problem-content sub {
                    vertical-align: sub;
                    font-size: 0.75em;
                }
                .problem-content table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 0.75em 0;
                }
                .problem-content th,
                .problem-content td {
                    border: 1px solid #e8e8e8;
                    padding: 8px 12px;
                    text-align: left;
                }
                .problem-content th {
                    background-color: #fafafa;
                    font-weight: 600;
                }
                /* KaTeX formula styles */
                .problem-content .katex-wrapper {
                    display: inline-block;
                    padding: 2px 4px;
                }
                .problem-content .katex {
                    font-size: 1.1em;
                }
                .problem-content .katex-display {
                    display: block;
                    margin: 1em 0;
                    text-align: center;
                }
                /* Column layout */
                .problem-content .column-block {
                    display: grid;
                    grid-auto-flow: column;
                    grid-auto-columns: 1fr;
                    gap: 16px;
                    margin: 12px 0;
                }
                .problem-content .column {
                    padding: 12px;
                    background-color: #fafafa;
                    border-radius: 4px;
                }
                @media (max-width: 600px) {
                    .problem-content .column-block {
                        grid-auto-flow: row;
                        grid-template-columns: 1fr;
                    }
                }
                /* Indent styles */
                .problem-content [data-indent="1"] {
                    padding-left: 2em;
                }
                .problem-content [data-indent="2"] {
                    padding-left: 4em;
                }
                .problem-content [data-indent="3"] {
                    padding-left: 6em;
                }
                /* Custom scrollbar */
                .problem-statement-viewer::-webkit-scrollbar {
                    width: 8px;
                }
                .problem-statement-viewer::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .problem-statement-viewer::-webkit-scrollbar-thumb {
                    background: #7cbbf5;
                    border-radius: 4px;
                }
                .problem-statement-viewer::-webkit-scrollbar-thumb:hover {
                    background: #5aa8f0;
                }
            `}</style>
        </div>
    );
}
